import { FC, memo, useCallback, useMemo, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import {
  ColumnDef,
  OnChangeFn,
  SortingState,
  TableOptions,
  getCoreRowModel,
} from '@tanstack/react-table'
import { skipToken } from '@reduxjs/toolkit/query'

import {
  useDeleteTaskTemplateMutation,
  useGetTaskTemplatesQuery,
} from '@crew/apis/taskTemplate/taskTemplateApis'
import { TaskTemplate } from '@crew/apis/taskTemplate/models/getTaskTemplates/response'
import { TaskTemplateScope } from '@crew/enums/domain'
import { Region, SettingKeyType } from '@crew/enums/app'
import { useTranslation } from '@crew/modules/i18n'
import { useUserSetting } from '@crew/states'
import { useValueChangeEffect } from '@crew/hooks'

import { useAppDispatch, useAppSelector } from 'states/hooks'
import {
  ObjectEventMessage,
  notifyTaskTemplateEvent,
} from 'features/app/states/appSlice'
import { NotifyEventType } from 'enums/app'

import { useShowApiErrors } from 'hooks/useShowApiErrors'
import { useToast } from 'hooks/useToast'
import { CrewTable } from 'components/elements/crewTable/crewTable'
import { TaskTemplateListItemActionButton } from './components/taskTemplateListItemActionButton/taskTemplateListItemActionButton'
import { useModal } from 'components/layouts/modal/useModal'
import { TaskTemplateEntryDialog } from 'features/task/components/taskTemplateEntryDialog/taskTemplateEntryDialog'
import qs from 'qs'
import { getParamAsArray } from 'utils'
import Lock from '~icons/material-symbols/lock'
import PublicIcon from '~icons/material-symbols/public'
import { usePaidFeatureStatus } from 'hooks/usePaidFeatureStatus'

// タスクテンプレートをリスト表示する時に登録日を表示するためのコンポーネント
// renderとして使うのでmemo不可
const CreatedAt: FC<{ date: string }> = (props) => {
  const [t] = useTranslation()

  // ユーザー設定からデフォルトのユーザープロファイル地域を取得
  const defaultUserProfileRegion = useUserSetting(
    SettingKeyType.UserProfileRegion,
    Region.Japan.value
  )

  return (
    <>
      {props
        ? t('format.timestamp', {
            value: props.date,
            timeZone: defaultUserProfileRegion,
          })
        : undefined}
    </>
  )
}

// タスクテンプレートをリスト表示するコンポーネント
export const TaskTemplateListGrid = memo(() => {
  const { projectId } = useParams()
  const { t } = useTranslation()
  const { success } = useToast()
  const [showApiErrors] = useShowApiErrors()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  const [searchParams] = useSearchParams()
  const params = qs.parse(searchParams.toString())

  // 有償機能が利用可能かどうかを判定する
  const paidFeatureStatus = usePaidFeatureStatus()

  // テンプレート一覧取得
  const { data: taskTemplates, refetch: refetchTaskTemplates } =
    useGetTaskTemplatesQuery(
      projectId
        ? {
            scope: TaskTemplateScope.Private, // プロジェクトの管理タブ内なので、プロジェクトに紐付くもののみ取得する
            projectId: projectId,
            keyword: undefined,
            sort: getParamAsArray('sort', params) ?? undefined,
          }
        : skipToken
    )

  // 初期読込、及び更新を検知して再フェッチ
  const taskTemplateEventMessage = useAppSelector(
    (state) => state.app.taskTemplateEventMessage
  )
  useValueChangeEffect(
    () => {
      // projectIdが存在する場合はrefetchする。
      // projectIdが存在しない場合、Queryは無効なのでrefetchできない
      projectId && refetchTaskTemplates()
    },
    [projectId, refetchTaskTemplates],
    taskTemplateEventMessage
  )

  // 変更イベントハンドラ。登録・更新・削除イベントの共通処理
  const handleChange = useCallback(
    async (eventType: NotifyEventType) => {
      const eventMessage: ObjectEventMessage<TaskTemplate> = {
        eventType,
        // aEventMessageハンドラでは`id`を使用しないため空欄とする
        id: '',
        object: undefined,
      }
      dispatch(notifyTaskTemplateEvent(eventMessage))
    },
    [dispatch]
  )

  // 登録・更新されたイベントハンドラ
  const handleSubmit = useCallback(async () => {
    await handleChange(NotifyEventType.Inserted)
  }, [handleChange])

  // 削除されたイベントハンドラ
  const handleDeleted = useCallback(async () => {
    await handleChange(NotifyEventType.Deleted)
  }, [handleChange])

  const [lazyDeleteTaskTemplateMutation] = useDeleteTaskTemplateMutation()

  // タスクテンプレート入力ダイアログのモーダル制御
  const [
    isTaskTemplateEntryDialogOpen,
    openTaskTemplateEntryDialog,
    closeTaskTemplateEntryDialog,
  ] = useModal()

  // 編集ダイアログのため、対象のテンプレートIDを保持するstate
  const [selectedTaskTemplateId, setSelectedTaskTemplateId] = useState<
    string | null
  >(null)

  // 表中の編集アイコンが押されたイベントハンドラ
  const handleEdit = useCallback(
    (target: TaskTemplate) => {
      setSelectedTaskTemplateId(target.id)
      openTaskTemplateEntryDialog()
    },
    [openTaskTemplateEntryDialog]
  )

  // 表中の削除アイコンが押されたイベントハンドラ
  const handleDelete = useCallback(
    async (target: TaskTemplate) => {
      try {
        await lazyDeleteTaskTemplateMutation({ id: target.id }).unwrap()
        await handleDeleted()
        success(t('message.task.taskTemplateDeleted'))
      } catch (err) {
        showApiErrors(err)
      }
    },
    [handleDeleted, lazyDeleteTaskTemplateMutation, showApiErrors, success, t]
  )

  // ソート状態
  const sorting: SortingState = useMemo(() => {
    const sortArray = getParamAsArray('sort', params) || []
    return sortArray.map((sort) => {
      const [id, direction] = sort.split('.')
      return {
        id,
        desc: direction === 'desc',
      }
    })
  }, [params])

  // ソート変更時の処理
  const handleSortingChange: OnChangeFn<SortingState> = useCallback(
    (updaterOrValue) => {
      let values: SortingState
      if (updaterOrValue instanceof Function) {
        values = updaterOrValue(sorting)
      } else {
        values = updaterOrValue
      }

      const sortList = values.map((sort) => {
        return `${sort.id}.${sort.desc ? 'desc' : 'asc'}`
      })

      const newParams = {
        ...params,
        sort: sortList,
      }

      navigate(`?${qs.stringify(newParams, { arrayFormat: 'repeat' })}`)
    },
    [navigate, params, sorting]
  )

  // カラム設定
  const columns = useMemo<ColumnDef<TaskTemplate>[]>(() => {
    const originalColumns: ColumnDef<TaskTemplate>[] = [
      // 公開範囲
      {
        id: 'scope',
        accessorKey: 'scope',
        header: '',
        cell: ({ row }) => {
          const template = row.original
          const isPrivate = template.scope === TaskTemplateScope.Private
          return isPrivate ? (
            <Lock width={16} height={16} />
          ) : (
            <PublicIcon width={16} height={16} />
          )
        },
        size: 10,
        meta: {
          align: 'center',
        },
        enableSorting: true,
      },
      {
        id: 'name',
        accessorKey: 'name',
        header: () => t('label.name'),
        cell: ({ row }) => <div className="w-full">{row.original.name}</div>,
        minSize: 50,
        enableSorting: true,
      },
      {
        id: 'createdAt',
        accessorKey: 'createdAt',
        header: () => t('label.registrationDatetime'),
        cell: ({ row }) => (
          <div className="truncate">
            <CreatedAt date={row.original.createdAt} />
          </div>
        ),
        size: 150,
        minSize: 150,
        enableSorting: true,
      },
    ]

    // 条件によって変動するカラム
    // 編集・削除列
    const actionColumns: ColumnDef<TaskTemplate>[] = [
      {
        id: 'action',
        accessorKey: 'action',
        header: '',
        cell: ({ row }) => (
          <div className="flex flex-1 justify-center">
            <TaskTemplateListItemActionButton
              target={row.original}
              onEdit={handleEdit}
              onDelete={handleDelete}
            />
          </div>
        ),
        size: 50,
        minSize: 50,
        enableSorting: false,
      },
    ]

    if (paidFeatureStatus.canUsePaidFeature) {
      // 有償プランが利用可能な場合は、編集・削除カラムを追加
      originalColumns.push(...actionColumns)
    }

    return originalColumns
  }, [handleDelete, handleEdit, paidFeatureStatus.canUsePaidFeature, t])

  // テーブルオプション設定
  const tableOptions: TableOptions<TaskTemplate> = {
    data: taskTemplates?.taskTemplates ?? [],
    columns,
    columnResizeMode: 'onChange',
    getCoreRowModel: getCoreRowModel(),
    // ソートを有効化
    state: {
      sorting,
    },
    onSortingChange: handleSortingChange,
    manualSorting: true,
    enableMultiSort: true,
    maxMultiSortColCount: 2,
    meta: {
      headerRowHeight: 35,
      dataRowHeight: 40,
    },
  }

  return (
    <div className="h-full flex flex-col">
      {taskTemplates && (
        <CrewTable tableOptions={tableOptions} showPager={false} />
      )}
      {projectId && selectedTaskTemplateId && (
        <TaskTemplateEntryDialog
          isEditMode={true}
          title={t('label.taskTemplate.editTaskTemplate')}
          projectId={projectId}
          taskTemplateId={selectedTaskTemplateId}
          isOpen={isTaskTemplateEntryDialogOpen}
          onClose={closeTaskTemplateEntryDialog}
          onSubmit={handleSubmit}
          onDeleted={handleDeleted}
        />
      )}
    </div>
  )
})
